import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTitle } from 'react-use';

import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Button } from '@/components/ui/button';
import useSegments from '@/segment/useSegments';
import { PageName } from '@/shared/types/segment';

import { BarriersToProgress } from './BarriersToProgress';
import { CallDurationChart } from './CallDurationChart';
import { CampaignResults } from './CampaignResults';
import { findMetricValue, groupMetrics } from './metricConfig';
import { OutreachConversion } from './OutreachConversion';
import { PerformanceStrip } from './PerformanceStrip';
import { PromisesOverTime } from './PromisesOverTime';
import { useCampaignAnalytics } from '../../hooks/useCampaignAnalytics';
import { useCampaignCallDuration } from '../../hooks/useCampaignCallDuration';
import { useCampaigns } from '../../hooks/useCampaigns';
import { CampaignWrapper } from '../CampaignWrapper';

/**
 * Campaign "Performance" tab — campaign results, the outreach conversion funnel,
 * promise curves and the barriers to progress, from a single analytics call.
 * `CampaignDetailLayout` only routes `payment_recovery` campaigns here.
 */
export const CampaignPerformance = () => {
  const { t } = useTranslation();
  const { campaignId } = useParams<{ campaignId: string }>();

  const { campaign } = useCampaigns(campaignId);

  // The layout's redirect only fires once the campaign has loaded, so this page renders
  // first even for types that will bounce to Overview. Everything with a side effect —
  // the page-view event and the two requests — waits until we know the type.
  const isRecoveryCampaign = campaign?.type === 'payment_recovery';
  const analyticsId = isRecoveryCampaign ? campaignId : undefined;

  const { analytics, isLoading, isError, refetch } = useCampaignAnalytics(analyticsId);
  const currency = analytics?.currency ?? undefined;
  const {
    data: callDurationBuckets,
    isLoading: isCallDurationLoading,
    isError: isCallDurationError,
  } = useCampaignCallDuration(analyticsId);

  useTitle(t('pages.campaigns_performance', { 0: campaign?.name ?? '-' }));

  // Null until the type is known: the other types bounce to Overview.
  useSegments(isRecoveryCampaign ? PageName.CAMPAIGN_PERFORMANCE : null);

  const { resultTiles, promisedDebts, disputes, exclusions, leftover } = groupMetrics(
    analytics?.metrics
  );

  // Null unless both sides are present and the denominator is non-zero.
  const promised = findMetricValue(analytics?.metrics, 'total_ptp_amount');
  const debt = findMetricValue(analytics?.metrics, 'debt_amount');
  const promisedShare = promised == null || !debt ? null : promised / debt;

  if (isError) {
    return (
      <PageContentWrapper>
        <CampaignWrapper>
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-foreground-muted">{t('common.something_wrong')}</p>
            <Button onClick={() => refetch()} size="sm" variant="outline-neutral">
              {t('common.reload')}
            </Button>
          </div>
        </CampaignWrapper>
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper>
      <CampaignWrapper>
        <div className="flex flex-col gap-8">
          <OutreachConversion isLoading={isLoading} stages={analytics?.funnel ?? []} />

          <CampaignResults
            currency={currency}
            isLoading={isLoading}
            promisedDebts={promisedDebts}
            promisedShare={promisedShare}
            tiles={resultTiles}
          />

          <PromisesOverTime
            currency={currency}
            dailyPtp={analytics?.daily_ptp}
            isLoading={isLoading}
          />

          <BarriersToProgress disputes={disputes} exclusions={exclusions} isLoading={isLoading} />

          {/* Safety net: a metric key the client does not know about still renders. */}
          {leftover.length > 0 ? <PerformanceStrip currency={currency} metrics={leftover} /> : null}

          {/* Half-width column, like the promises and barriers cards above it. */}
          <div className="grid gap-6 lg:grid-cols-2">
            <CallDurationChart
              buckets={callDurationBuckets}
              isError={isCallDurationError}
              isLoading={isCallDurationLoading}
            />
          </div>
        </div>
      </CampaignWrapper>
    </PageContentWrapper>
  );
};
